import dagre from "@dagrejs/dagre";
import { keyOf } from "@p0security/shared/graph/graph";
import { NodeFlowRenderer } from "components/Assessment/components/GraphVisualization";

import { Collapsed, CollapsedNode, GraphEdge } from "./collapse";

type LayoutArgs<G extends object, P> = {
  nodes: CollapsedNode<G>[];
  edges: Readonly<GraphEdge<CollapsedNode<G>>[]>;
  maxPathLength: number;
  render: NodeFlowRenderer<G & Collapsed<G>, P>;
  renderArgs: P;
};

export const layout = <G extends object, P>({
  nodes,
  edges,
  maxPathLength,
  render,
  renderArgs,
}: LayoutArgs<G, P>) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    // Inter-node separation in pixels
    nodesep: 24,
    edgesep: 0,
    rankdir: "LR",
    // `tight-tree` is ~4-5x faster than the default `network-simplex` algo,
    // at the expense of sometimes leaving intermediate nodes placed too high
    // or too low. But for some customers this can turn layout times from a minute
    // to 15 seconds.
    ranker: "tight-tree",
    // adjust horizontal spacing based on path length to best use horizontal real estate
    ranksep: maxPathLength < 4 ? 160 : maxPathLength < 8 ? 130 : 100,
  });
  for (const node of nodes) {
    const { height, width } = render[node.type].size(node as any, renderArgs);
    dagreGraph.setNode(keyOf(node), {
      width: width,
      height: height,
    });
  }
  for (const [parent, child] of edges) {
    dagreGraph.setEdge(keyOf(parent), keyOf(child));
  }

  // This consumes the majority of render time. Since the library is synchronous, there is
  // unfortunately no way to prevent it from blocking the main UI render without resorting to
  // a web worker
  dagre.layout(dagreGraph);

  return dagreGraph;
};
